// One agent run's detail (agents.getRun): what it was asked, how it ended, each step, its answer, and
// the model's cost for the owner only (agents.manage); a flag in words, as the approvals card says it.
// Opened from Activity today and from Recent runs.

import { sentences } from '@flowology/kernel';
import { Loading } from '../shared/Loading.tsx';
import { Lock } from '../shared/ApprovalsInbox.tsx';
import { actionWords, clock, taskText } from './model.ts';
import { costText, flagWords, runStatus, triggerWords } from './runs-model.ts';
import type { RunDetail } from './types.ts';
import { useQuery } from '../books/ui.tsx';

export function RunPanel({
  runId,
  timeZone,
  showCost,
}: {
  runId: string;
  timeZone?: string | undefined;
  /** The model's cost: the owner's (agents.manage). */
  showCost: boolean;
}) {
  const run = useQuery<RunDetail>('agents.getRun', { runId });
  if (!run.data) return <Loading error={run.error} what="the run" retry={() => void run.reload()} />;
  const r = run.data;
  return (
    <div className="panel-quiet run">
      <p className="small">
        <b>{taskText(r.task)}</b> · {triggerWords(r.trigger)} · {runStatus(r.status)[0]}
        {r.stopReason ? ` (${r.stopReason})` : ''} · {r.steps} steps
        {showCost && r.costCents !== undefined ? ` · model cost ${costText(r.costCents)}` : ''}
      </p>
      {flagWords(r.flags).map((f) => (
        <p key={f} className="chip say bad">
          {f}
        </p>
      ))}
      {r.hidden && (
        <p className="lockwhy small">
          <Lock /> {r.hidden}
        </p>
      )}
      <ol className="steps">
        {r.stepsDetail.map((s) => (
          <li key={s.n}>
            <span className="muted small">{s.n}</span>
            {s.kind === 'model' ? (
              <span className="small">
                {s.outcome === 'answer' ? 'The model answered.' : 'The model picked its next tool.'}
              </span>
            ) : (
              <span className="small">
                <span title={s.action ?? undefined}>{actionWords(s.action, s.actionTitle)}</span>{' '}
                {s.outcome.replace(/_/g, ' ')}
                {s.input !== null && s.input !== undefined && (
                  <code className="cmd">{JSON.stringify(s.input)}</code>
                )}
              </span>
            )}
          </li>
        ))}
      </ol>
      {r.answer && <p className="small">Answer: {r.answer}</p>}
      <p className="muted small">
        {sentences(
          `Started ${clock(r.startedAt, timeZone)}${r.endedAt ? `, ended ${clock(r.endedAt, timeZone)}` : ''}`,
        )}
      </p>
    </div>
  );
}
